interface DomainMeta {
  environment: 'dev' | 'stage' | 'uat' | 'prod';
  monolithOrigin: string;
  tenant: string;
  recruiterUiOrigin: string;
}

// Searches for any domain such as source.ap-1.fountain.com. Will not match `source.fountain.com`.
const FOUNTAIN_DOMAIN_MATCHER = /^source\.[^.]*\.fountain\.com/;
// Matches the staging domains such as source.staging-use-05.fountain.com
const FOUNTAIN_STAGING_DOMAIN_MATCHER = /^source\.staging[^.]*\.fountain\.com/;

const FOUNTAIN_DEV_DOMAINS = new Set([
  'source.dev-01.fountain.com',
  'source.dev-02.fountain.com',
  'source.faut-02.fountain.com',
]);

const FOUNTAIN_UAT_DOMAINS = new Set([
  'source.uat-01.fountain.com',
  'source.faut-01.fountain.com',
]);

const isStagingDomain = (hostname: string) =>
  FOUNTAIN_STAGING_DOMAIN_MATCHER.test(hostname);

const isUatDomain = (hostname: string) => FOUNTAIN_UAT_DOMAINS.has(hostname);

const isDevDomain = (hostname: string) => FOUNTAIN_DEV_DOMAINS.has(hostname);

const getMonolithOrigin = (hostname: string) =>
  `https://${hostname.replace('source.', '')}`;

const getRecruiterUiOrigin = (hostname: string) =>
  `https://${hostname.replace('source', 'app')}`;

const getTenant = (hostname: string) => hostname.split('.')[1];

const buildMeta = (
  environment: DomainMeta['environment'],
  hostname: string,
): DomainMeta => ({
  environment,
  monolithOrigin: getMonolithOrigin(hostname),
  tenant: getTenant(hostname),
  recruiterUiOrigin: getRecruiterUiOrigin(hostname),
});

export const inferDomainMeta = (hostname: string): DomainMeta => {
  if (hostname === 'localhost') {
    return {
      environment: 'dev',
      monolithOrigin: `http://localhost:${
        process.env.NODE_ENV === 'test' ? 3023 : 3000
      }`,
      tenant: 'local',
      recruiterUiOrigin: 'http://localhost:3333',
    };
  }

  if (isDevDomain(hostname)) return buildMeta('dev', hostname);
  if (isStagingDomain(hostname)) return buildMeta('stage', hostname);
  if (isUatDomain(hostname)) return buildMeta('uat', hostname);

  if (hostname === 'source.fountain.com') {
    return {
      environment: 'prod',
      monolithOrigin: 'https://web.fountain.com',
      tenant: 'fountain',
      recruiterUiOrigin: getRecruiterUiOrigin(hostname),
    };
  }

  if (FOUNTAIN_DOMAIN_MATCHER.test(hostname)) {
    return buildMeta('prod', hostname);
  }

  throw new Error('Unable to map hostname to a known domain');
};

export const domainMeta = inferDomainMeta(window.location.hostname);
